/** Which days a recurring rule produces (ARCHITECTURE 8.10.5). The DATABASE decides and generates the jobs (private.recurrence_matches /
 *  private.expand_recurrence); this twin lets the planner SEE the next dates of a rule while they write it, with no network. It is proven equal
 *  to the SQL function on random rules by ops/contracts/check.mjs, so the preview cannot promise a day the server will not make.
 *  Dates are 'YYYY-MM-DD' strings; all arithmetic is on whole calendar days (no time zone, no clock). */
export type Freq = 'daily' | 'weekly' | 'monthly' | 'every_n_days'
export interface RuleVersion {
  version_no: number
  freq: Freq
  interval_n: number
  by_weekday: readonly number[] | null
  by_monthday: readonly number[] | null
  season_start_md: string | null
  season_end_md: string | null
  valid_from: string
  valid_to: string | null
  exdates: readonly string[]
  /** a rotation of setting overrides, advanced once per day the rule produces */
  rotation?: readonly unknown[] | null
}

const parse = (iso: string): { y: number; m: number; d: number } => ({ y: Number(iso.slice(0, 4)), m: Number(iso.slice(5, 7)), d: Number(iso.slice(8, 10)) })
/** Days since 1970-01-01. */
export const dayNumber = (iso: string): number => { const { y, m, d } = parse(iso); return Math.round(Date.UTC(y, m - 1, d) / 86400000) }
export const dayFromNumber = (n: number): string => new Date(n * 86400000).toISOString().slice(0, 10)
export const addDays = (iso: string, n: number): string => dayFromNumber(dayNumber(iso) + n)
/** 1 = Monday .. 7 = Sunday (ISO). */
export const isoWeekday = (iso: string): number => { const w = new Date(dayNumber(iso) * 86400000).getUTCDay(); return w === 0 ? 7 : w }
const lastDayOfMonth = (iso: string): number => { const { y, m } = parse(iso); return new Date(Date.UTC(y, m, 0)).getUTCDate() }

/** Does this version produce an occurrence on `day`? Same arithmetic, case by case, as private.recurrence_matches. */
export function recurrenceMatches(v: RuleVersion, day: string): boolean {
  if (day < v.valid_from || (v.valid_to !== null && day > v.valid_to) || v.exdates.includes(day)) return false
  if (v.season_start_md !== null && v.season_end_md !== null) {
    const md = day.slice(5)
    const inSeason = v.season_start_md <= v.season_end_md ? md >= v.season_start_md && md <= v.season_end_md : md >= v.season_start_md || md <= v.season_end_md
    if (!inSeason) return false
  }
  const since = dayNumber(day) - dayNumber(v.valid_from)
  switch (v.freq) {
    case 'daily': case 'every_n_days': return since % v.interval_n === 0
    case 'weekly': {
      const days = v.by_weekday ?? [isoWeekday(v.valid_from)]
      const weekStart = dayNumber(v.valid_from) - (isoWeekday(v.valid_from) - 1)
      return days.includes(isoWeekday(day)) && Math.floor((dayNumber(day) - weekStart) / 7) % v.interval_n === 0
    }
    case 'monthly': {
      const days = v.by_monthday ?? [parse(v.valid_from).d]
      const { y, m, d } = parse(day), f = parse(v.valid_from)
      const onDay = days.includes(d) || (days.includes(-1) && d === lastDayOfMonth(day))
      return onDay && ((y * 12 + m) - (f.y * 12 + f.m)) % v.interval_n === 0
    }
    default: return false
  }
}

export interface Occurrence { date: string; version_no: number; rotation_index: number }

/** The occurrences of one recurrence between two days (inclusive). The version that decides a day is the HIGHEST number that has started by
 *  then, and its own end date ends the rule: an older version never takes over again. The rotation counts the deciding version's own days. */
export function expandRule(versions: readonly RuleVersion[], from: string, to: string): Occurrence[] {
  const sorted = [...versions].sort((a, b) => b.version_no - a.version_no)
  const out: Occurrence[] = []
  for (let n = dayNumber(from); n <= dayNumber(to); n++) {
    const day = dayFromNumber(n)
    const v = sorted.find((x) => x.valid_from <= day)
    if (!v || !recurrenceMatches(v, day)) continue
    let idx = 0
    if (v.rotation && v.rotation.length > 0) {
      let count = 0
      for (let k = dayNumber(v.valid_from); k < n; k++) if (recurrenceMatches(v, dayFromNumber(k))) count++
      idx = count % v.rotation.length
    }
    out.push({ date: day, version_no: v.version_no, rotation_index: idx })
  }
  return out
}
